import { useEffect, useRef } from "react";
import "./DistributionChart.css";
import { percentilesFor, gaussianPdf, isMonotonic, fitGaussian, nFactor, maxNFor } from "./distributionStats";
import { SYNTHETIC_FIELD_NEEDLES, syntheticReasonText } from "./syntheticFields";

const fmt = (n) => "£" + Math.round(n).toLocaleString("en-GB");
const fmtK = (n) => (n >= 1000 ? "£" + Math.round(n / 1000) + "k" : "£" + Math.round(n));

// FIXED 2026-08-05: same fix as RangeBarsChart.jsx's computeXDomain() -
// distXMax used to be a hardcoded 100000, so any territory whose Max (or
// whose fitted curve's mu + 3.2*sigma tail) exceeds £100k got plotted past
// the SVG's own coordinate space and bled off the page (overflow:visible
// draws it anyway, just outside the chart panel). Domain is now derived
// from the real data, rounded up to a round step, with 100000 kept as a
// floor so small datasets keep the original spacing.
function computeXDomain(data) {
  const dataMax = Math.max(100000, ...data.map((d) => d.max || 0));
  const step = dataMax <= 200000 ? 20000 : dataMax <= 500000 ? 50000 : 100000;
  const xMax = Math.ceil(dataMax / step) * step;
  const ticks = [];
  for (let t = 0; t <= xMax; t += step) ticks.push(t);
  return { xMax, ticks };
}

// FIXED 2026-08-05: sorting by territory (UK, Ireland, South Africa,
// Romania, India, Brazil) now happens once in JobDetailTerritoryQuarterRow.jsx
// before the `data` prop reaches this component - no longer needed here.

// SYNTHETIC_FIELD_NEEDLES/syntheticReasonText moved out to
// syntheticFields.js on 2026-08-05 (same move as distributionStats.js) so
// CostBandTable.jsx's new per-field calculator icon can reuse the exact
// logic tipHTML() (below) already uses for the "Synthetic Data:" section.

// Statistics (erf/probit/Gaussian fit, √n scaling) moved out to
// distributionStats.js on 2026-08-05 so keyInsights.js (Key Insights card)
// can reuse the exact same fit logic instead of a second, drifting copy -
// see that file for the full derivation notes. snErf was dead code (never
// called anywhere in this file) and was dropped in the move, not kept.

// Same tooltip shape as RangeBarsChart.jsx's tipHTML, reused as-is so
// hovering either chart feels identical.
// FIXED 2026-08-05: same fix as RangeBarsChart.jsx's tipHTML - Full range EE
// / Core band EE always render, Synthetic Data (Base 10%/Base 90%) is a
// separate additive section, not a replacement. See the note there.
function tipHTML(d) {
  const p = percentilesFor(d.territory_id);
  const row = (label, val) =>
    `<div class="tt-row"><span class="tt-label">${label}</span><span class="tt-val">${fmt(val)}</span></div>`;
  const rowP = (label, val, pct) =>
    `<div class="tt-row"><span class="tt-label">${label} <span style="opacity:.55">P${pct}</span></span><span class="tt-val">${fmt(val)}</span></div>`;

  const eeRows = `
    <div class="tt-row"><span class="tt-label">Full range EE</span><span class="tt-val">${d.full_range_ee_count?.toLocaleString("en-GB") ?? "-"}${d.low_n ? ' <span style="color:var(--dc-accent);font-style:italic">*small sample</span>' : ""}</span></div>
    <div class="tt-row"><span class="tt-label">Core band EE <span style="opacity:.55">≈20%</span></span><span class="tt-val">${d.core_band_ee_count?.toLocaleString("en-GB") ?? "-"}</span></div>`;

  const base10 = syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.min);
  const base90 = syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.max);
  const syntheticRows =
    base10 || base90
      ? `<div class="tt-divider"></div>
         <div class="tt-label" style="margin-bottom:4px">Synthetic Data:</div>
         ${base10 ? `<div class="tt-row"><span class="tt-label">Base 10%</span><span class="tt-val">${base10}</span></div>` : ""}
         ${base90 ? `<div class="tt-row"><span class="tt-label">Base 90%</span><span class="tt-val">${base90}</span></div>` : ""}`
      : "";

  return `<div class="tt-title">${d.territory}</div>
    ${rowP("Min", d.min, p.min)}
    ${rowP("NTR", d.ntr, p.ntr)}
    ${rowP("Exp", d.exp, p.exp)}
    ${rowP("Vexp", d.vexp, p.vexp)}
    ${rowP("Max", d.max, p.max)}
    <div class="tt-divider"></div>
    ${row("Full range spread", d.full_range_spread)}
    ${row("Core band spread", d.core_band_spread)}
    <div class="tt-divider"></div>
    ${eeRows}
    ${syntheticRows}`;
}

const W = 1100;
const H = 540;
const M = { top: 36, right: 50, bottom: 60, left: 160 };

// FIXED 2026-08-05 (3rd territory/quarter attempt): data/loading are now
// plain props from App.js instead of an internal fetch - same fix and same
// reasoning as RangeBarsChart.jsx (see that file's note above its component
// signature). This chart reuses /api/cost-band-detail rather than a new
// endpoint, since it's the same underlying percentile data - now fetched
// once, in JobDetailTerritoryQuarterRow.jsx, shared with CostBandTable.jsx/
// RangeBarsChart.jsx instead of fetched a third time here.
export default function DistributionChart({ filters, data, loading }) {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  const jobTitleId = filters?.jobTitle?.id;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || data.length === 0) {
      if (svg) svg.innerHTML = "";
      return;
    }

    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;
    const { xMax: distXMax, ticks } = computeXDomain(data);
    const x = (v) => M.left + (v / distXMax) * innerW;

    const rowH = innerH / data.length;
    const baselineOffset = 18;
    // FIXED 2026-08-05: was an unconditional rowH*0.78. With a single
    // territory selected, rowH becomes the entire innerH and this produced
    // a curve peak taller than the actual headroom between the row's
    // baseline and the chart's own top edge (M.top) - the curve rendered
    // off the top of the SVG canvas ("going above and beyond", reproduced
    // for UK/India when filtered to one territory). Clamped to the
    // worst-case headroom - the topmost row (i=0) has the least room above
    // it, since baseline moves further from M.top for every row after it -
    // so capping to that headroom keeps every row's curve inside the plot
    // area regardless of how many rows are showing.
    const topRowHeadroom = rowH / 2 + baselineOffset;
    const maxCurveH = Math.min(rowH * 0.78, Math.max(0, topRowHeadroom - 4));

    const maxN = maxNFor(data);
    // FIXED 2026-08-05: always fit a curve, for every territory - dropped
    // the `d.synthetic ? null : ...` branch. See nFactor()'s note above.
    const fits = data.map((d) => fitGaussian(d, percentilesFor(d.territory_id)));

    let maxPdf = 0;
    data.forEach((d, i) => {
      // Degenerate (non-monotonic) rows don't get a real fit - excluded
      // here so they can't skew the shared height scale for every other
      // row's curve. See isMonotonic()'s note above.
      if (!isMonotonic(d) || !(fits[i].sigma > 0)) return;
      const peak = gaussianPdf(fits[i].mu, fits[i].mu, fits[i].sigma) * nFactor(d, maxN);
      if (peak > maxPdf) maxPdf = peak;
    });
    if (maxPdf === 0) maxPdf = 1; // every row degenerate - avoid divide-by-zero below

    let html = "";
    ticks.forEach((t) => {
      html += `<line class="dc-grid-line" x1="${x(t)}" x2="${x(t)}" y1="${M.top}" y2="${M.top + innerH}" />`;
      html += `<text class="dc-axis-label" x="${x(t)}" y="${M.top + innerH + 22}" text-anchor="middle">${fmtK(t)}</text>`;
    });
    html += `<text class="dc-axis-label" x="${M.left + innerW / 2}" y="${H - 8}" text-anchor="middle" style="letter-spacing:.12em">ANNUAL FULLY LOADED COST · GBP</text>`;
    html += `<line class="dc-axis-line" x1="${M.left}" x2="${M.left + innerW}" y1="${M.top + innerH}" y2="${M.top + innerH}"/>`;
    html += `<line class="dc-axis-line" x1="${M.left}" x2="${M.left}" y1="${M.top}" y2="${M.top + innerH}"/>`;

    const steps = 220;

    data.forEach((d, i) => {
      const yCenter = M.top + rowH * i + rowH / 2;
      const baseline = yCenter + baselineOffset;
      const delay = i * 70 + 100;
      // Still used below for the subText fallback label only (kept from the
      // earlier employee-count-first fix) - no longer used to pick a
      // rendering branch, since every row now always gets a curve.
      const isSynthetic = d.synthetic;
      const p = percentilesFor(d.territory_id);
      const isSpecial = p.exp !== 50;
      const fit = fits[i];
      const color = "var(--dc-navy-mid)";
      const factor = nFactor(d, maxN);
      const degenerate = !isMonotonic(d) || !(fit.sigma > 0);

      const y = (pdfVal) => baseline - ((pdfVal * factor) / maxPdf) * maxCurveH;

      html += `<g class="dc-row-group" data-i="${i}">`;
      html += `<rect class="dc-row-hover-target" x="${M.left}" y="${M.top + rowH * i}" width="${innerW}" height="${rowH}" />`;

      if (degenerate) {
        // Markers-only fallback (see isMonotonic()'s note) - no curve/fill/
        // tails, since the fit is meaningless for this row. Still shows
        // Min/Max as baseline ticks and NTR/Exp/Vexp as markers sitting on
        // the baseline, so the row stays legible instead of blank.
        html += `<line x1="${x(d.min)}" x2="${x(d.min)}" y1="${baseline}" y2="${baseline - 8}" stroke="${color}" stroke-width="1.4" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 400}ms forwards"/>`;
        html += `<line x1="${x(d.max)}" x2="${x(d.max)}" y1="${baseline}" y2="${baseline - 8}" stroke="${color}" stroke-width="1.4" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 400}ms forwards"/>`;
        html += `<circle class="dc-tier-marker" cx="${x(d.ntr)}" cy="${baseline}" r="4" style="transform-origin:${x(d.ntr)}px ${baseline}px; animation: dcPop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 800}ms backwards"/>`;
        html += `<circle class="dc-tier-marker" cx="${x(d.vexp)}" cy="${baseline}" r="4" style="transform-origin:${x(d.vexp)}px ${baseline}px; animation: dcPop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 840}ms backwards"/>`;
        html += `<circle class="dc-median-marker" cx="${x(d.exp)}" cy="${baseline}" r="5.5" style="transform-origin:${x(d.exp)}px ${baseline}px; animation: dcPop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 880}ms backwards"/>`;
        html += `<text class="dc-range-cap" x="${x(d.min) - 6}" y="${baseline + 14}" text-anchor="end" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 900}ms forwards">${fmtK(d.min)}</text>`;
        html += `<text class="dc-range-cap" x="${x(d.max) + 6}" y="${baseline + 14}" text-anchor="start" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 900}ms forwards">${fmtK(d.max)}</text>`;
      } else {
        const xLoFull = Math.max(0, fit.mu - 3.2 * fit.sigma);
        const xHiFull = Math.min(distXMax, fit.mu + 3.2 * fit.sigma);
        const xLoBand = Math.max(xLoFull, d.min);
        const xHiBand = Math.min(xHiFull, d.max);
        const totalExtent = xHiFull - xLoFull;

        function buildSeg(xs, xe) {
          const segSteps = Math.max(20, Math.round((steps * (xe - xs)) / totalExtent));
          let p2 = "";
          for (let k = 0; k <= segSteps; k++) {
            const xv = xs + ((xe - xs) * k) / segSteps;
            const py = gaussianPdf(xv, fit.mu, fit.sigma);
            p2 += (k === 0 ? "M" : "L") + x(xv).toFixed(1) + "," + y(py).toFixed(1) + " ";
          }
          return p2;
        }

        const hasLeftTail = xLoFull < xLoBand - 1;
        const hasRightTail = xHiBand < xHiFull - 1;

        if (hasLeftTail) {
          const leftCurve = buildSeg(xLoFull, xLoBand);
          const leftFill = `M${x(xLoFull).toFixed(1)},${baseline} ` + leftCurve.slice(1) + ` L${x(xLoBand).toFixed(1)},${baseline} Z`;
          html += `<path d="${leftFill}" fill="${color}" fill-opacity="0.12" style="opacity:0; animation: dcFadeIn .9s ease ${delay + 400}ms forwards"/>`;
          html += `<path d="${leftCurve}" stroke="${color}" stroke-width="1.4" stroke-dasharray="3 3" fill="none" style="opacity:0; animation: dcFadeIn .9s ease ${delay + 400}ms forwards"/>`;
        }

        const centerCurve = buildSeg(xLoBand, xHiBand);
        const centerFill = `M${x(xLoBand).toFixed(1)},${baseline} ` + centerCurve.slice(1) + ` L${x(xHiBand).toFixed(1)},${baseline} Z`;
        html += `<path class="dist-fill" d="${centerFill}" fill="${color}" fill-opacity="0.34" style="animation: dcFadeIn .9s ease ${delay + 200}ms backwards"/>`;
        html += `<path class="dist-curve" d="${centerCurve}" stroke="${color}" stroke-width="1.8" stroke-dasharray="3000" style="animation: dcDrawCurve 1.3s cubic-bezier(.4,.0,.2,1) ${delay}ms backwards"/>`;

        if (hasRightTail) {
          const rightCurve = buildSeg(xHiBand, xHiFull);
          const rightFill = `M${x(xHiBand).toFixed(1)},${baseline} ` + rightCurve.slice(1) + ` L${x(xHiFull).toFixed(1)},${baseline} Z`;
          html += `<path d="${rightFill}" fill="${color}" fill-opacity="0.12" style="opacity:0; animation: dcFadeIn .9s ease ${delay + 400}ms forwards"/>`;
          html += `<path d="${rightCurve}" stroke="${color}" stroke-width="1.4" stroke-dasharray="3 3" fill="none" style="opacity:0; animation: dcFadeIn .9s ease ${delay + 400}ms forwards"/>`;
        }

        const yMinEdge = y(gaussianPdf(d.min, fit.mu, fit.sigma));
        const yMaxEdge = y(gaussianPdf(d.max, fit.mu, fit.sigma));
        html += `<line x1="${x(d.min)}" x2="${x(d.min)}" y1="${baseline}" y2="${yMinEdge}" stroke="${color}" stroke-width="1.4" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 600}ms forwards"/>`;
        html += `<line x1="${x(d.max)}" x2="${x(d.max)}" y1="${baseline}" y2="${yMaxEdge}" stroke="${color}" stroke-width="1.4" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 600}ms forwards"/>`;

        const yNtr = y(gaussianPdf(d.ntr, fit.mu, fit.sigma));
        const yExp = y(gaussianPdf(d.exp, fit.mu, fit.sigma));
        const yVexp = y(gaussianPdf(d.vexp, fit.mu, fit.sigma));

        html += `<line x1="${x(d.ntr)}" x2="${x(d.ntr)}" y1="${baseline}" y2="${yNtr}" stroke="${color}" stroke-width="1.2" stroke-dasharray="2 2" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 700}ms forwards"/>`;
        html += `<line x1="${x(d.exp)}" x2="${x(d.exp)}" y1="${baseline}" y2="${yExp}" stroke="${color}" stroke-width="1.7" stroke-dasharray="2 2" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 700}ms forwards"/>`;
        html += `<line x1="${x(d.vexp)}" x2="${x(d.vexp)}" y1="${baseline}" y2="${yVexp}" stroke="${color}" stroke-width="1.2" stroke-dasharray="2 2" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 700}ms forwards"/>`;

        html += `<circle class="dc-tier-marker" cx="${x(d.ntr)}" cy="${yNtr}" r="4" style="transform-origin:${x(d.ntr)}px ${yNtr}px; animation: dcPop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 800}ms backwards"/>`;
        html += `<circle class="dc-tier-marker" cx="${x(d.vexp)}" cy="${yVexp}" r="4" style="transform-origin:${x(d.vexp)}px ${yVexp}px; animation: dcPop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 840}ms backwards"/>`;
        html += `<circle class="dc-median-marker" cx="${x(d.exp)}" cy="${yExp}" r="5.5" style="transform-origin:${x(d.exp)}px ${yExp}px; animation: dcPop .5s cubic-bezier(.2,1.2,.4,1) ${delay + 880}ms backwards"/>`;

        html += `<text class="dc-range-cap" x="${x(d.min) - 6}" y="${baseline + 14}" text-anchor="end" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 900}ms forwards">${fmtK(d.min)}</text>`;
        html += `<text class="dc-range-cap" x="${x(d.max) + 6}" y="${baseline + 14}" text-anchor="start" style="opacity:0; animation: dcFadeIn .6s ease ${delay + 900}ms forwards">${fmtK(d.max)}</text>`;
      }

      const yLabelCenter = baseline - maxCurveH * 0.5;
      html += `<text class="dc-terr-label" x="${M.left - 16}" y="${yLabelCenter - 2}" text-anchor="end">${d.territory}</text>`;
      const isLowN = d.low_n;
      // Ported from compensation-range-chart.component.ts's buildFootnote() -
      // real employee count is checked FIRST, synthetic label is only a
      // fallback when there's no count at all. See the same fix/note in
      // RangeBarsChart.jsx.
      const subText = d.full_range_ee_count
        ? `n=${d.full_range_ee_count}${isLowN ? '<tspan fill="var(--dc-accent)" font-style="italic" font-weight="600" dx="2" dy="-3" font-size="9">*</tspan>' : ""}${isSpecial ? " · shifted" : ""}`
        : isSynthetic
        ? `synthetic · business rules${isSpecial ? " · shifted" : ""}`
        : "";
      html += `<text class="dc-terr-sub" x="${M.left - 16}" y="${yLabelCenter + 14}" text-anchor="end">${subText}</text>`;

      html += `</g>`;
    });

    svg.innerHTML = html;

    svg.querySelectorAll(".dc-row-group").forEach((g) => {
      const i = +g.dataset.i;
      g.addEventListener("mousemove", (e) => showTip(tipHTML(data[i]), e));
      g.addEventListener("mouseleave", () => hideTip());
    });

    function showTip(html, e) {
      const tt = tooltipRef.current;
      if (!tt) return;
      tt.innerHTML = html;
      tt.classList.add("on");
      const pad = 14;
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = tt.offsetWidth, h = tt.offsetHeight;
      let left = e.clientX + pad;
      let top = e.clientY + pad;
      if (left + w > vw - 8) left = e.clientX - pad - w;
      if (top + h > vh - 8) top = e.clientY - pad - h;
      tt.style.left = left + "px";
      tt.style.top = top + "px";
    }
    function hideTip() {
      tooltipRef.current?.classList.remove("on");
    }
  }, [data]);

  return (
    <section className="dc-section">
      <div className="dc-chart-panel">
        <div className="dc-chart-head">
          <div>
            <div className="dc-chart-pretitle">Annual fully loaded cost · GBP · Fitted distribution</div>
            <div className="dc-chart-title">Gaussian curves fitted to percentile data per territory.</div>
          </div>
        </div>

        <div className="dc-legend">
          <div className="dc-legend-item"><span className="dc-swatch band"></span>Reported pay band (P10–P90)</div>
          <div className="dc-legend-item"><span className="dc-swatch tail"></span>Extrapolated tails (below P10 / above P90)</div>
          <div className="dc-legend-item"><span className="dc-swatch-bars"></span>Curve height scaled by √n</div>
          <div className="dc-legend-item"><span className="dc-swatch-marker"></span>NTR / Vexp percentile</div>
          <div className="dc-legend-item"><span className="dc-swatch-marker exp"></span>Exp percentile</div>
        </div>

        {!jobTitleId && <p className="dc-status">Select a job title to see the fitted distribution.</p>}
        {jobTitleId && loading && <p className="dc-status">Loading…</p>}
        {jobTitleId && !loading && data.length === 0 && <p className="dc-status">No data for this selection.</p>}

        {jobTitleId && !loading && data.length > 0 && (
          <svg ref={svgRef} className="dc-chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" aria-label="Fitted distribution chart" />
        )}
      </div>
      <div ref={tooltipRef} className="dc-tooltip" role="tooltip"></div>
    </section>
  );
}
